import * as React from "react";
import styled, { css } from "styled-components";
import { lighten } from "polished";
import { toast } from "react-toastify";
import _ from "lodash";

import {
  DeleteSettingParams,
  useInheritedSettingsApi,
  UpdateSettingParams,
  CreateSettingParams
} from "src/core/shared/api/inheritedSettings.api";
import { IInheritedSetting } from "src/core/drops/api/drops.api";
import { IApiError } from "src/library/utils";

import Grid from "../Hedron";
import { Text } from "../Text";
import Label from "./Label";
import { Switch } from "./Switch";

export const CascadeSettingFieldLabel = styled(Label)`
  display: flex;
  padding-bottom: 0;

  span {
    padding-left: 10px;
    line-height: 20px;
  }
`;

// Used to determine if an update is required on this setting
export type UpdatedSettingsFields = Array<{
  params: Pick<IInheritedSetting, "name"> & Partial<Omit<IInheritedSetting, "name">>;
  action: "create" | "update" | "delete"
}>;

export interface CascadeSettingFieldProps {
  label: string | number | React.ReactNode;
  defaultChecked: boolean;
  setDefaultChecked: (checked: boolean) => void;
  SettingElement: React.ReactNode;
  name: string;
  disabled?: boolean;
  erp: "seedless" | "unity" | "rjl" | "odoo" | "zertus" | null;
  // The following props manage API requests needing to be handled following updates to settings
  settingsToUpdate: UpdatedSettingsFields;
  updateSettingsToUpdate: React.Dispatch<React.SetStateAction<UpdatedSettingsFields>>;
  updateIsSubmiting: React.Dispatch<React.SetStateAction<boolean>>;
  updateRequestsCount: React.Dispatch<React.SetStateAction<number | null>>;
  updateErrors: React.Dispatch<React.SetStateAction<IApiError[]>>
}

const CascadeSettingField: React.ReactFCWithChildren<CascadeSettingFieldProps> = ({
  label,
  defaultChecked,
  setDefaultChecked,
  SettingElement,
  name,
  disabled,
  erp,
  settingsToUpdate,
  updateSettingsToUpdate,
  updateIsSubmiting,
  updateRequestsCount,
  updateErrors
}) => {
  const [ updateSettingResponse, updateSetting ] = useInheritedSettingsApi("UPDATE_SETTING");
  const [ createSettingResponse, createSetting ] = useInheritedSettingsApi("CREATE_SETTING");
  const [ deleteSettingResponse, deleteSetting ] = useInheritedSettingsApi("DELETE_SETTING");

  // Helper for handling any of the API responses
  const handleResponse = React.useCallback((response: {
    complete: boolean;
    error: IApiError | null;
  }) => {
    if (response.complete) {
      updateRequestsCount(prevRequestCount => prevRequestCount ? prevRequestCount - 1 : 0);
    }

    if (response.error) {
      updateErrors(prevErrors => response.error ? [ response.error, ...prevErrors ] : prevErrors);
    }
  }, [ updateErrors, updateRequestsCount ]);

  // Handle changes to settings that need to be updated via API request
  React.useEffect(() => {
    if (!settingsToUpdate.length) {
      return;
    }

    settingsToUpdate.forEach(setting => {
      // Check to see if this setting field is included in list of fields that need updating
      if (setting.params.name === name) {
        // Run relevant API action
        switch (setting.action) {
          case "create": {
            createSetting(setting.params as CreateSettingParams);
            break;
          }
          case "update": {
            updateSetting(setting.params as UpdateSettingParams);
            break;
          }
          case "delete": {
            deleteSetting(setting.params as DeleteSettingParams);
            break;
          }
          default:
            toast.error("Unhandled setting action");
            break;
        }

        // Clear the setting from settingsToUpdate list so as not to loop api request
        updateSettingsToUpdate(prevSettingsToUpdate => _.filter(prevSettingsToUpdate, setting => setting.params.name !== name));
      }
    });
  }, [
    createSetting,
    deleteSetting,
    name,
    settingsToUpdate,
    updateSetting,
    updateSettingsToUpdate
  ]);

  // Keep parent updated on if any request is submitting
  React.useEffect(() => {
    updateIsSubmiting(updateSettingResponse.pending || createSettingResponse.pending || deleteSettingResponse.pending);
  }, [
    updateSettingResponse.pending,
    createSettingResponse.pending,
    deleteSettingResponse.pending,
    updateIsSubmiting
  ]);

  React.useEffect(() => {
    handleResponse(updateSettingResponse);
  }, [ handleResponse, updateSettingResponse ]);

  React.useEffect(() => {
    handleResponse(deleteSettingResponse);
  }, [ handleResponse, deleteSettingResponse ]);

  React.useEffect(() => {
    handleResponse(createSettingResponse);
  }, [ handleResponse, createSettingResponse ]);

  return (
    <CascadeSettingFieldWrapper direction="vertical">
      {erp === "unity" && disabled && <SetByERPText />}

      <Grid.Box>
        <StyledRadioRow direction="vertical">
          <Text
            as="p"
            className="default-value"
          >
            <Text weight="bold">
              Default:
            </Text>

            {" "}

            {label}
          </Text>

          <CascadeSettingFieldLabel
            for={`${name}-default`}
            label="Override setting"
          />

          <div className="settings-override-switch">
            <Switch
              name={name}
              disabled={disabled}
              onChange={e => {
                setDefaultChecked(!e);
              }}
              checked={!defaultChecked}
            />
          </div>
        </StyledRadioRow>
      </Grid.Box>

      <Grid.Box />

      {!defaultChecked && (
        <div className={`setting-element ${defaultChecked ? "inactive" : "active"}`}>
          {SettingElement}
        </div>
      )}

      <Grid.Box />
    </CascadeSettingFieldWrapper>
  );
};

const StyledRadioRow = styled(Grid.Bounds)`
  align-items: flex-start;
  > input {
    ${({ theme }) =>
    css`
        margin: ${theme.spacing.margins.md} 0;
        margin-right: ${theme.spacing.margins.md};
      `};
  }
`;

const SetByERPWrapper = styled(Grid.Box)`
  ${({ theme }) =>
    css`
      background: ${lighten(0.2, theme.colors.warn)};
      border: 1px solid ${theme.colors.midGrey};
      padding: ${theme.spacing.padding.sm};
      font-style: italic;
    `}
`;

const CascadeSettingFieldWrapper = styled(Grid.Bounds)`
${({ theme }) =>
    css`
      .settings-override-switch {
        display: block;
        padding-left: ${theme.spacing.padding.sm};
      }

      .setting-element {
        margin-top: ${theme.spacing.padding.sm};
        padding-left: ${theme.spacing.padding.sm};
      }
  `}
`;

export const SetByERPText: React.ReactFCWithChildren = () => {
  return (
    <SetByERPWrapper>
      <Text>
        This setting is controlled by your ERP and connot be changed through the Mezze portal.
      </Text>
    </SetByERPWrapper>
  );
};

export default CascadeSettingField;
